/**
 * Centralized error handling utility for the web application
 * Provides graceful error handling, recovery mechanisms, and user-friendly messages
 */

import { i18n } from '@lumora/common';

export interface ErrorInfo {
  code?: string;
  message: string;
  context?: Record<string, unknown>;
  userId?: string;
  timestamp?: string;
  recoverable?: boolean;
}

export interface RecoveryAction {
  label: string;
  action: () => void | Promise<void>;
  primary?: boolean;
}

export interface ErrorHandlerOptions {
  showToast?: boolean;
  customMessage?: string;
  context?: Record<string, unknown>;
  recoveryActions?: RecoveryAction[];
  logError?: boolean;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorListeners: Array<(error: ErrorInfo) => void> = [];

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Handle authentication state errors with recovery mechanisms
   */
  handleAuthError(
    error: unknown,
    options: ErrorHandlerOptions = {}
  ): ErrorInfo {
    const errorInfo = this.normalizeError(error);

    // Add authentication-specific context
    errorInfo.context = {
      ...errorInfo.context,
      ...options.context,
      errorType: 'authentication',
    };

    // Determine if error is recoverable
    errorInfo.recoverable = this.isAuthErrorRecoverable(errorInfo.code);

    // Add recovery actions for auth errors
    if (errorInfo.recoverable && !options.recoveryActions) {
      options.recoveryActions = this.getAuthRecoveryActions(errorInfo.code);
    }

    return this.handleError(errorInfo, options);
  }

  /**
   * Handle goal creation errors with user-friendly messages
   */
  handleGoalCreationError(
    error: unknown,
    options: ErrorHandlerOptions = {}
  ): ErrorInfo {
    const errorInfo = this.normalizeError(error);

    errorInfo.context = {
      ...errorInfo.context,
      ...options.context,
      errorType: 'goalCreation',
    };

    // Always recoverable for goal creation
    errorInfo.recoverable = true;

    // Add goal creation specific recovery actions
    if (!options.recoveryActions) {
      options.recoveryActions = [
        {
          label: i18n.t('errors.goalCreation.recovery.tryAgain'),
          action: () => window.location.reload(),
          primary: true,
        },
        {
          label: i18n.t('errors.goalCreation.recovery.skipForNow'),
          action: () => {
            // Navigate to dashboard without goals
            window.location.href = '/dashboard';
          },
        },
      ];
    }

    // Use user-friendly message for goal creation
    if (!options.customMessage) {
      options.customMessage = this.getGoalCreationErrorMessage(errorInfo.code);
    }

    return this.handleError(errorInfo, options);
  }

  /**
   * Handle inconsistent user state with recovery mechanisms
   */
  handleInconsistentState(
    stateType: 'onboarding' | 'goals' | 'profile',
    details: Record<string, unknown>,
    options: ErrorHandlerOptions = {}
  ): ErrorInfo {
    const errorInfo: ErrorInfo = {
      code: `INCONSISTENT_${stateType.toUpperCase()}_STATE`,
      message: `Inconsistent ${stateType} state detected`,
      context: {
        stateType,
        details,
        ...options.context,
        errorType: 'inconsistentState',
      },
      recoverable: true,
      timestamp: new Date().toISOString(),
    };

    // Add state-specific recovery actions
    if (!options.recoveryActions) {
      options.recoveryActions = this.getStateRecoveryActions(stateType);
    }

    if (!options.customMessage) {
      options.customMessage = this.getStateErrorMessage(stateType);
    }

    return this.handleError(errorInfo, options);
  }

  /**
   * Core error handling method
   */
  handleError(
    errorInfo: ErrorInfo,
    options: ErrorHandlerOptions = {}
  ): ErrorInfo {
    // Log error if enabled (default: true)
    if (options.logError !== false) {
      this.logError(errorInfo);
    }

    // Notify error listeners
    this.notifyErrorListeners(errorInfo);

    // Show user notification if enabled
    if (options.showToast !== false) {
      this.showErrorNotification(errorInfo, options);
    }

    return errorInfo;
  }

  /**
   * Normalize different error types into ErrorInfo
   */
  private normalizeError(error: unknown): ErrorInfo {
    if (typeof error === 'string') {
      return {
        message: error,
        timestamp: new Date().toISOString(),
      };
    }

    if (error instanceof Error) {
      return {
        message: error.message,
        code: error.name,
        timestamp: new Date().toISOString(),
      };
    }

    if (typeof error === 'object' && error !== null) {
      const errorObj = error as Record<string, unknown>;
      return {
        message: (errorObj.message as string) || 'Unknown error',
        code: errorObj.code as string,
        context: errorObj.context as Record<string, unknown>,
        timestamp: new Date().toISOString(),
      };
    }

    return {
      message: 'Unknown error occurred',
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Check if authentication error is recoverable
   */
  private isAuthErrorRecoverable(code?: string): boolean {
    const recoverableAuthErrors = [
      'NETWORK_ERROR',
      'TIMEOUT_ERROR',
      'SESSION_EXPIRED',
      'TOKEN_REFRESH_FAILED',
      'TEMPORARY_ERROR',
    ];
    return code ? recoverableAuthErrors.includes(code) : true;
  }

  /**
   * Get recovery actions for authentication errors
   */
  private getAuthRecoveryActions(code?: string): RecoveryAction[] {
    switch (code) {
      case 'SESSION_EXPIRED':
      case 'TOKEN_REFRESH_FAILED':
        return [
          {
            label: i18n.t('errors.authentication.recovery.signInAgain'),
            action: () => {
              window.location.href = '/auth';
            },
            primary: true,
          },
        ];
      case 'NETWORK_ERROR':
      case 'TIMEOUT_ERROR':
        return [
          {
            label: i18n.t('errors.authentication.recovery.retry'),
            action: () => window.location.reload(),
            primary: true,
          },
          {
            label: i18n.t('errors.authentication.recovery.continueOffline'),
            action: () => {
              // Allow limited functionality
              console.log('Continuing in offline mode');
            },
          },
        ];
      default:
        return [
          {
            label: i18n.t('errors.authentication.recovery.refreshPage'),
            action: () => window.location.reload(),
            primary: true,
          },
        ];
    }
  }

  /**
   * Get recovery actions for inconsistent state
   */
  private getStateRecoveryActions(stateType: string): RecoveryAction[] {
    switch (stateType) {
      case 'onboarding':
        return [
          {
            label: i18n.t(
              'errors.inconsistentState.recovery.continueToDashboard'
            ),
            action: () => {
              window.location.href = '/dashboard';
            },
            primary: true,
          },
          {
            label: i18n.t(
              'errors.inconsistentState.recovery.restartOnboarding'
            ),
            action: () => {
              window.location.href = '/onboarding';
            },
          },
        ];
      case 'goals':
        return [
          {
            label: i18n.t('errors.inconsistentState.recovery.createGoalNow'),
            action: () => {
              window.location.href = '/goals?create=true';
            },
            primary: true,
          },
          {
            label: i18n.t(
              'errors.inconsistentState.recovery.continueWithoutGoals'
            ),
            action: () => {
              window.location.href = '/dashboard';
            },
          },
        ];
      case 'profile':
        return [
          {
            label: i18n.t('errors.inconsistentState.recovery.updateProfile'),
            action: () => {
              window.location.href = '/profile';
            },
            primary: true,
          },
          {
            label: i18n.t('errors.inconsistentState.recovery.continueAnyway'),
            action: () => {
              console.log('Continuing with incomplete profile');
            },
          },
        ];
      default:
        return [
          {
            label: i18n.t('errors.inconsistentState.recovery.refreshPage'),
            action: () => window.location.reload(),
            primary: true,
          },
        ];
    }
  }

  /**
   * Get user-friendly error messages for goal creation
   */
  private getGoalCreationErrorMessage(code?: string): string {
    switch (code) {
      case 'VALIDATION_ERROR':
        return i18n.t('errors.goalCreation.validationError');
      case 'NETWORK_ERROR':
        return i18n.t('errors.goalCreation.networkError');
      case 'PERMISSION_ERROR':
        return i18n.t('errors.goalCreation.permissionError');
      case 'QUOTA_EXCEEDED':
        return i18n.t('errors.goalCreation.quotaExceeded');
      default:
        return i18n.t('errors.goalCreation.default');
    }
  }

  /**
   * Get user-friendly error messages for state issues
   */
  private getStateErrorMessage(stateType: string): string {
    switch (stateType) {
      case 'onboarding':
        return i18n.t('errors.inconsistentState.onboarding');
      case 'goals':
        return i18n.t('errors.inconsistentState.goals');
      case 'profile':
        return i18n.t('errors.inconsistentState.profile');
      default:
        return i18n.t('errors.inconsistentState.default');
    }
  }

  /**
   * Log error for debugging and monitoring
   */
  private logError(errorInfo: ErrorInfo): void {
    console.error('Error handled:', {
      ...errorInfo,
      userAgent: navigator.userAgent,
      url: window.location.href,
    });

    // TODO: Send to monitoring service (e.g., Sentry, LogRocket)
    // This would be implemented based on the monitoring solution chosen
  }

  /**
   * Show user-friendly error notification
   */
  private showErrorNotification(
    errorInfo: ErrorInfo,
    options: ErrorHandlerOptions
  ): void {
    const message = options.customMessage || errorInfo.message;

    // Dispatch custom event for toast system to handle
    const event = new CustomEvent('showErrorToast', {
      detail: {
        message,
        title: this.getErrorTitle(errorInfo.context?.errorType as string),
        recoveryActions: options.recoveryActions,
        persistent: !errorInfo.recoverable,
      },
    });

    window.dispatchEvent(event);
  }

  /**
   * Get appropriate error title based on error type
   */
  private getErrorTitle(errorType?: string): string {
    switch (errorType) {
      case 'authentication':
        return i18n.t('errors.authentication.title');
      case 'goalCreation':
        return i18n.t('errors.goalCreation.title');
      case 'inconsistentState':
        return i18n.t('errors.inconsistentState.title');
      default:
        return i18n.t('errors.genericTitle');
    }
  }

  /**
   * Add error listener for custom error handling
   */
  addErrorListener(listener: (error: ErrorInfo) => void): () => void {
    this.errorListeners.push(listener);
    return () => {
      const index = this.errorListeners.indexOf(listener);
      if (index > -1) {
        this.errorListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all error listeners
   */
  private notifyErrorListeners(errorInfo: ErrorInfo): void {
    this.errorListeners.forEach(listener => {
      try {
        listener(errorInfo);
      } catch (error) {
        console.error('Error in error listener:', error);
      }
    });
  }

  /**
   * Create a retry handler for async operations
   */
  createRetryHandler<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
  ): () => Promise<T> {
    return async () => {
      let lastError: unknown;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          return await operation();
        } catch (error) {
          lastError = error;

          if (attempt === maxRetries) {
            throw error;
          }

          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
      }

      throw lastError;
    };
  }

  /**
   * Wrap async function with error handling
   */
  withErrorHandling<T extends (...args: unknown[]) => Promise<unknown>>(
    fn: T,
    options: ErrorHandlerOptions = {}
  ): T {
    return (async (...args: Parameters<T>) => {
      try {
        return await fn(...args);
      } catch (error) {
        this.handleError(this.normalizeError(error), options);
        throw error;
      }
    }) as T;
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Utility functions for common error scenarios
export const withRetry = <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  return errorHandler.createRetryHandler(operation, maxRetries, delay)();
};

export const handleAsyncError = async <T>(
  operation: () => Promise<T>,
  errorMessage: string = 'Operation failed'
): Promise<T | null> => {
  try {
    return await operation();
  } catch (error) {
    errorHandler.handleError(errorHandler['normalizeError'](error), {
      customMessage: errorMessage,
    });
    return null;
  }
};
